import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { logCheckoutException } from "@/utils/checkout-sentry";
import { Fragment, useEffect, useState } from "react";

import { useSession } from "../providers/session";

import { currency } from "@/utils/formats";
import Spinner from "@/components/assets/spinner.svg";
import { PaymentMethods } from "@/components/checkout/methods";
import OnlineCourseBasketItem from "@/components/checkout/online-course-basket-item";
import LoyaltyPointsApply from "@/components/checkout/loyalty-points-apply";
import type { Entry } from "@/components/checkout/online-course";
import {
  trackOnlineBeginCheckout,
  trackOnlineEvent,
  type OnlineCourseAnalyticsItem,
} from "@/utils/online-course-tracking";
import {
  isOnlineBogofCampaignActive,
  ONLINE_BOGOF_DISCOUNT_LABEL,
  partitionOnlineBasketForBogof,
  resolveOnlineCheckoutDiscount,
} from "@/lib/online-course-bogof";
import { basketLineTotalPence, buildCheckoutPayload } from "@/lib/online-basket-checkout";
import { mergeClientAttribution } from "@/utils/client-attribution";
import { getOnlineCourseLayout } from "@/lib/online-course-layout";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export default function OnlineBookingCheckout({
  open,
  setOpen,
  ...rest
}: Props) {
  const { onlineCourses, userData } = useSession();
  const classicStorefront = getOnlineCourseLayout() === "classic";

  const [state, setState] = useState<{
    loading: boolean;
    error: any;
    additional: any[];
    appliedPoints: number;
    email: string;
    emailError: string | null;
  }>({
    loading: false,
    error: null,
    additional: [],
    appliedPoints: 0,
    email: typeof userData?.email === "string" ? userData.email : "",
    emailError: null,
  });

  useEffect(() => {
    if (typeof userData?.email !== "string" || !userData.email) {
      return;
    }

    setState((current) =>
      current.email.trim() ? current : { ...current, email: userData.email },
    );
  }, [userData?.email]);

  // Browser back from Stripe restores the page from bfcache with loading still true.
  useEffect(() => {
    const clearCheckoutLoading = () => {
      setState((current) =>
        current.loading ? { ...current, loading: false } : current,
      );
    };

    window.addEventListener("pageshow", clearCheckoutLoading);
    return () => {
      window.removeEventListener("pageshow", clearCheckoutLoading);
    };
  }, []);

  const additionalSubtotal = calculateAdditionalSubtotal(state.additional);
  const basketPartition = partitionOnlineBasketForBogof(
    onlineCourses,
    additionalSubtotal,
  );
  const discount = resolveOnlineCheckoutDiscount({
    eligibleCoursePricesPence: basketPartition.eligibleCoursePricesPence,
    courseLineItemCount: calculateOnlineLineItemCount(
      onlineCourses,
      state.additional,
    ),
    subtotalPence: basketPartition.subtotalPence,
  });
  const total = Math.max(
    0,
    discount.finalTotalPence - (classicStorefront ? 0 : state.appliedPoints),
  );
  const availablePoints = userData?.points ?? 0;

  const priceIds = onlineCourses
    .filter((course) => !course.fields.configurationId)
    .map((course) => course.fields.pricing?.id)
    .filter(Boolean);

  const handleSubmit = async () => {
    const missingOption = onlineCourses.find(
      (course) =>
        course.fields.optionsManaged && !course.fields.configurationId,
    );

    if (missingOption) {
      setState((s) => ({
        ...s,
        loading: false,
        error: `Choose a training option for ${missingOption.fields.title} before checkout.`,
      }));
      return;
    }

    const trimmedEmail = state.email.trim();
    if (!trimmedEmail) {
      setState((s) => ({
        ...s,
        loading: false,
        emailError: "Enter your email address to continue.",
        error: null,
      }));
      return;
    }

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setState((s) => ({
        ...s,
        loading: false,
        emailError: "Enter a valid email address.",
        error: null,
      }));
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null, emailError: null }));

    trackOnlineBeginCheckout({
      items: buildOnlineBasketAnalyticsItems(onlineCourses, state.additional),
      valuePence: total,
      discountPence: discount.discountPence,
      source: "basket",
    });

    if (discount.kind === "bogof" && discount.discountPence > 0) {
      trackOnlineEvent("online_bogof_applied", {
        eligible_count: discount.eligibleCount,
        free_count: discount.freeCount,
        discount_pence: discount.discountPence,
        source: "basket",
      });
    }

    const checkoutPayload = buildCheckoutPayload(
      onlineCourses,
      state.additional,
      Mapping,
    );

    try {
      const req = await fetch("/api/e-learning/create-checkout", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          configurationItems: checkoutPayload.configurationItems,
          priceIds: checkoutPayload.priceIds,
          additional: checkoutPayload.additional,
          metadata: mergeClientAttribution(checkoutPayload.metadata),
          ...(classicStorefront || state.appliedPoints <= 0
            ? {}
            : { points: state.appliedPoints }),
          customerEmail: trimmedEmail,
        }),
      });
      const res = await req.json();
      const errorMessage =
        typeof res.error === "string"
          ? res.error
          : res.error?.message || res.message;
      if (!req.ok || !res.url) {
        throw new Error(
          errorMessage || "Failed to create checkout session. Please try again.",
        );
      }
      window.location.href = res.url;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to start checkout. Please try again.";
      console.log(err);
      logCheckoutException(err, "online_panel");
      setState((s) => ({ ...s, loading: false, error: message }));
    }
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto h-screen w-screen max-w-full sm:max-w-xl lg:max-w-2xl xl:max-w-3xl">
                    <section
                      className="h-full w-full bg-white shadow-xl"
                      id="booking"
                    >
                      <h1 className="sr-only">Checkout</h1>
                      <div className="flex flex-col h-[100vh]  overflow-hidden">
                        <section
                          id="order-summary"
                          className="flex-1 px-4 pt-4 w-full h-full flex flex-col lg:px-8 pb-4 mx-auto border shadow"
                        >
                          <div className="flex justify-between py-2">
                            <div>
                              <h2 className="text-2xl mb-1 font-medium">
                                Complete your online course purchase
                              </h2>
                              <p className="text-sm text-gray-500">
                                Review your basket, add any relevant extras, and
                                continue to secure checkout.
                              </p>
                            </div>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="-m-2 p-2 text-gray-400 hover:text-gray-500"
                                onClick={() => setOpen(false)}
                              >
                                <span className="sr-only">Close panel</span>
                                <XMarkIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden pt-4">
                            <div className="flow-root flex-1">
                              {(!onlineCourses ||
                                (onlineCourses &&
                                  onlineCourses.length === 0)) && (
                                <div className="flex flex-col space-y-4">
                                  <p>
                                    You have not added any online training to
                                    your basket yet.
                                  </p>
                                  <div className="">
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => setOpen(false)}
                                    >
                                      Continue browsing
                                    </button>
                                  </div>
                                </div>
                              )}
                              {onlineCourses && (
                                <>
                                  <ul
                                    role="list"
                                    className="-my-6 divide-y divide-gray-200"
                                  >
                                    {onlineCourses.map((onlineCourse, i) => {
                                      return (
                                        <li key={`service-${i}`}>
                                          <OnlineCourseBasketItem
                                            onlineCourse={onlineCourse}
                                            itemIndex={i}
                                          />
                                          {Mapping[
                                            onlineCourse.fields.pricing.id
                                          ] && (
                                            <div className="bg-brand-500 text-sm p-2 rounded-md text-white">
                                              <h3 className="font-semibold">
                                                {
                                                  Mapping[
                                                    onlineCourse.fields.pricing
                                                      .id
                                                  ].title
                                                }
                                              </h3>
                                              <p className="text-xs">
                                                {
                                                  Mapping[
                                                    onlineCourse.fields.pricing
                                                      .id
                                                  ].description
                                                }
                                              </p>
                                              <div className="flex items-end justify-end">
                                                <button
                                                  type="button"
                                                  className="text-xs text-center border p-2 mt-2 bg-gray-100 border-gray-200 text-gray-600 rounded-md"
                                                  onClick={() => {
                                                    trackOnlineEvent(
                                                      "online_upsell_accept",
                                                      {
                                                        source_course_slug:
                                                          onlineCourse.fields.url
                                                            ?.split("/")
                                                            .filter(Boolean)
                                                            .slice(-1)[0] ||
                                                          onlineCourse.sys?.id,
                                                        source_course_title:
                                                          onlineCourse.fields
                                                            .title,
                                                        upsell_cta:
                                                          Mapping[
                                                            onlineCourse.fields
                                                              .pricing.id
                                                          ].cta,
                                                        upsell_title:
                                                          Mapping[
                                                            onlineCourse.fields
                                                              .pricing.id
                                                          ].title,
                                                        accepted:
                                                          !state.additional.includes(
                                                            onlineCourse.fields
                                                              .pricing.id
                                                          ),
                                                      }
                                                    );

                                                    state.additional.includes(
                                                      onlineCourse.fields
                                                        .pricing.id
                                                    )
                                                      ? setState((s) => ({
                                                          ...s,
                                                          additional:
                                                            s.additional.filter(
                                                              (id) =>
                                                                id !==
                                                                onlineCourse
                                                                  .fields
                                                                  .pricing.id
                                                            ),
                                                        }))
                                                      : setState((s) => ({
                                                          ...s,
                                                          additional: [
                                                            ...s.additional,
                                                            onlineCourse.fields
                                                              .pricing.id,
                                                          ],
                                                        }));
                                                  }}
                                                >
                                                  {state.additional.includes(
                                                    onlineCourse.fields.pricing
                                                      .id
                                                  )
                                                    ? "Remove"
                                                    : Mapping[
                                                        onlineCourse.fields
                                                          .pricing.id
                                                      ].cta}
                                                </button>
                                              </div>
                                            </div>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                  <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="mt-8 w-full rounded-full border border-brand-200 bg-white px-5 py-3 text-sm font-semibold text-brand-700 transition-colors duration-200 hover:border-brand-500 hover:text-brand-800"
                                  >
                                    Continue browsing online courses
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="">
                            {onlineCourses && onlineCourses.length > 0 && (
                              <>
                                {/* <Voucher items={items} /> */}
                                <dl className="text-sm font-medium text-gray-500">
                                  <div className="flex flex-col gap-2 text-gray-900 pt-4">
                                    <div className="flex justify-between">
                                      <dt>Original subtotal</dt>
                                      <dd>{currency(basketPartition.subtotalPence)}</dd>
                                    </div>
                                    <div className="flex justify-between text-brand-700">
                                      <dt>
                                        {isOnlineBogofCampaignActive()
                                          ? discount.label ||
                                            ONLINE_BOGOF_DISCOUNT_LABEL
                                          : discount.kind === "bogof"
                                            ? discount.label
                                            : "Multi-course discount"}
                                      </dt>
                                      <dd>
                                        {discount.discountPence > 0
                                          ? `-${currency(discount.discountPence)}`
                                          : currency(0)}
                                      </dd>
                                    </div>
                                    {state.appliedPoints > 0 && !classicStorefront ? (
                                      <div className="flex justify-between text-brand-700">
                                        <dt>Loyalty points</dt>
                                        <dd>-{currency(state.appliedPoints)}</dd>
                                      </div>
                                    ) : null}
                                    <div className="flex justify-between border-t border-gray-200 pt-3 text-base font-semibold">
                                      <dt>Total</dt>
                                      <dd>{currency(total)}</dd>
                                    </div>
                                  </div>
                                </dl>
                                {!classicStorefront && availablePoints > 0 ? (
                                  <div className="mt-4">
                                    <LoyaltyPointsApply
                                      availablePoints={availablePoints}
                                      appliedPoints={state.appliedPoints}
                                      onApply={(points) =>
                                        setState((current) => ({
                                          ...current,
                                          appliedPoints: Math.min(
                                            points,
                                            discount.finalTotalPence,
                                          ),
                                        }))
                                      }
                                      onRemove={() =>
                                        setState((current) => ({
                                          ...current,
                                          appliedPoints: 0,
                                        }))
                                      }
                                      disabled={state.loading}
                                    />
                                  </div>
                                ) : null}
                                <div className="mt-4">
                                  <label
                                    htmlFor="online-basket-email"
                                    className="block text-sm font-semibold text-gray-900"
                                  >
                                    Email address
                                    <span className="sr-only"> (required)</span>
                                  </label>
                                  <p className="mt-1 text-xs text-gray-500">
                                    We will send your course access and receipt
                                    here.
                                  </p>
                                  <input
                                    id="online-basket-email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    required
                                    disabled={state.loading}
                                    value={state.email}
                                    onChange={(event) =>
                                      setState((current) => ({
                                        ...current,
                                        email: event.target.value,
                                        emailError: null,
                                      }))
                                    }
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        handleSubmit();
                                      }
                                    }}
                                    placeholder="you@example.com"
                                    aria-invalid={
                                      state.emailError ? true : undefined
                                    }
                                    aria-describedby={
                                      state.emailError
                                        ? "online-basket-email-error"
                                        : undefined
                                    }
                                    className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500"
                                  />
                                  {state.emailError ? (
                                    <p
                                      id="online-basket-email-error"
                                      role="alert"
                                      className="mt-2 text-sm text-red-700"
                                    >
                                      {state.emailError}
                                    </p>
                                  ) : null}
                                </div>
                              </>
                            )}
                            {onlineCourses.length > 0 ? (
                              <div className="flex flex-col justify-between mt-2 pb-12 sm:pb-0">
                                {state.error ? (
                                  <div
                                    role="alert"
                                    className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                                  >
                                    <p className="font-semibold text-red-900">
                                      Checkout unavailable
                                    </p>
                                    <p className="mt-1">{state.error}</p>
                                  </div>
                                ) : null}
                                <button
                                  disabled={state.loading}
                                  type="button"
                                  onClick={() => handleSubmit()}
                                  className="cursor-pointer block w-full rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center"
                                >
                                  {!state.loading && (
                                    <span className="relative z-[1] text-center">
                                      Go to secure checkout
                                    </span>
                                  )}
                                  {state.loading && (
                                    <span className="relative z-[1] text-center">
                                      Processing
                                    </span>
                                  )}
                                  {state.loading && (
                                    <span className="absolute right-1 z-[1]">
                                      <Spinner className="w-9 h-9" />
                                    </span>
                                  )}
                                </button>
                              </div>
                            ) : null}
                            <PaymentMethods />
                          </div>
                        </section>
                      </div>
                    </section>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

function calculateAdditionalSubtotal(additional: string[]) {
  if (!additional || additional.length === 0) {
    return 0;
  }

  return additional.reduce((total, id) => {
    const amount = Mapping[id]?.price_data?.unit_amount || 0;
    return total + amount;
  }, 0);
}

function calculateTotal(courses: Entry[], additional: any[]): number {
  let amt = 0;
  if (courses && courses.length > 0) {
    amt = courses.reduce(
      (total, course) => total + basketLineTotalPence(course),
      0,
    );
  }

  if (additional && additional.length > 0) {
    amt = additional.reduce((total, id) => {
      const amount = Mapping[id].price_data.unit_amount || 0;
      return total + amount;
    }, amt);
  }

  return amt;
}

function calculateOnlineLineItemCount(courses: Entry[], additional: any[]) {
  return (courses?.length || 0) + (additional?.length || 0);
}

function buildOnlineBasketAnalyticsItems(
  courses: Entry[],
  additional: any[]
): OnlineCourseAnalyticsItem[] {
  const courseItems = (courses || []).map((course) => {
    const priceId =
      course.fields.pricing?.id ||
      course.sys?.id ||
      course.fields.url ||
      course.fields.title;
    const category = Array.isArray(course.fields.categories)
      ? course.fields.categories[0]
      : course.fields.categories;

    return {
      item_id: priceId,
      item_name: course.fields.title,
      affiliation: "Cosmetic College",
      currency: "GBP",
      item_brand: "Cosmetic College",
      item_category: "Online Training Courses",
      item_category2: category || "online",
      price: (course.fields.pricing?.unit_amount || 0) / 100,
      quantity: 1,
      configuration_id: course.fields.configurationId || undefined,
      option_code: course.fields.optionCode || undefined,
    };
  });

  const additionalItems = (additional || [])
    .map((id) => {
      const item = Mapping[id];
      if (!item) {
        return null;
      }

      return {
        item_id: item.price_data.product || id,
        item_name: item.title,
        affiliation: "Cosmetic College",
        currency: "GBP",
        item_brand: "Cosmetic College",
        item_category: "Online Training Add-ons",
        price: (item.price_data.unit_amount || 0) / 100,
        quantity: 1,
      };
    })
    .filter(Boolean) as OnlineCourseAnalyticsItem[];

  return [...courseItems, ...additionalItems];
}

const Mapping = {
  // Skin Peels
  price_1Lbr8mAlQ13ioQgbMBxECkNT: {
    title: "Add Microneedling and save!",
    description:
      "Take your skills further with Microneedling at a special price",
    cta: "Add Microneedling for £39.99",
    // Microneedling
    price_data: {
      currency: "gbp",
      product: "prod_MJLQL3u7AtJZp1",
      unit_amount: 3999,
    },
  },
  // Dermaplaning
  price_1QixN1AlQ13ioQgbJmX9eXe6: {
    title: "Add LED Light Therapy and save!",
    description:
      "Take your skills further with LED Light Therapy at a special price",
    cta: "Add for £19.99",
    // Microneedling
    price_data: {
      currency: "gbp",
      product: "prod_MJLL8EcE2uGTcC",
      unit_amount: 1999,
    },
  },
  // Brow Lamination
  price_1QixQ5AlQ13ioQgbYRnBXab1: {
    title: "Add Lash Lift & Tint and save!",
    description:
      "Take your skills further with Lash Lift & Tint at a special price",
    cta: "Add for £19.99",
    // Microneedling
    price_data: {
      currency: "gbp",
      product: "prod_MJLP6cWMIiBsMY",
      unit_amount: 1999,
    },
  },
  // Microneedling
  price_1Laik3AlQ13ioQgbJZiIMI2k: {
    title: "Add BB Glow and save!",
    description: "Take your skills further with BB Glow at a special price",
    cta: "Add for £29.99",
    // Microneedling
    price_data: {
      currency: "gbp",
      product: "prod_MJODjPh2SyogUg",
      unit_amount: 2999,
    },
  },
  // Aqualyx
  price_1QixTIAlQ13ioQgbIGdifHZl: {
    title: "Add Lemon Bottle and save!",
    description:
      "Take your skills further with Lemon Bottle at a special price",
    cta: "Add for £39.99",
    // Microneedling
    price_data: {
      currency: "gbp",
      product: "prod_PjfjyDKzHYiZnc",
      unit_amount: 3999,
    },
  },
  // AP
  price_1MwXgkAlQ13ioQgbnoFrgGcS: {
    title: "Add Level 4 Laser and save!",
    description: "Take your skills further with Laser at a special price",
    cta: "Add for £399.99",
    // Microneedling
    price_data: {
      currency: "gbp",
      product: "prod_QRUvvf6zpds4hM",
      unit_amount: 39999,
    },
  },
  // PDO Mono
  price_1QizkvAlQ13ioQgbnWsSDSV7: {
    title: "Add PDO Cog Threads and save!",
    description:
      "Take your skills further with PDO Cog Threads at a special price",
    cta: "Add for £69.99",
    // PDO Cogs
    price_data: {
      currency: "gbp",
      product: "prod_RIreWj0v907o8K",
      unit_amount: 6999,
    },
  },
  // PDO Cogs
  price_1Qizk3AlQ13ioQgbPv3FP7Kx: {
    title: "Add PDO Mono Threads and save!",
    description:
      "Take your skills further with PDO Mono Threads at a special price",
    cta: "Add for £69.99",
    // PDO Mono
    price_data: {
      currency: "gbp",
      product: "prod_RIrdfAaZGrb3jo",
      unit_amount: 6999,
    },
  },
  // Mesotherapy
  price_1Qizm5AlQ13ioQgb8zt8uRqO: {
    title: "Add Mesotoxin and save!",
    description: "Take your skills further with Mesotoxin at a special price",
    cta: "Add for £69.99",
    // mesotoxin
    price_data: {
      currency: "gbp",
      product: "prod_RIrZqhSCXWMukU",
      unit_amount: 6999,
    },
  },
  price_1R46xLAlQ13ioQgbCutteHi7: {
    title: "Add Profhilo Face & Body and save!",
    description: "Take your skills further with Profhilo Face & Body at a special price",
    cta: "Add for £79.99",
    // mesotoxin
    price_data: {
      currency: "gbp",
      product: "prod_MeLKcoA5jRWi2X",
      unit_amount: 7999,
    },
  },
};
